import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, MapPin, Plus } from 'lucide-react';
import axios from 'axios';
import { API } from '../constants/site';
import { resolveImg } from '../constants/siteImages';
import { LOCAL_PAGES } from '../constants/localPages';
import { translations } from '../i18n/translations';
import { useLanguage } from '../i18n/LanguageContext';
import { useSEO } from '../components/SEO';
import { useSiteImage } from '../components/SiteImages';
import { Reveal, MaskedTitle, Overline, EASE } from '../components/motion';
import { LeadForm } from '../components/LeadForm';
import { PanelSketch } from '../components/PanelSketch';
import { PvcProfiles } from '../components/PvcProfiles';
import { NzebSection } from '../components/NzebSection';

export default function LocalPage({ pageKey }) {
  const cfg = LOCAL_PAGES[pageKey];
  const { t } = useLanguage();
  const lp = t(cfg.tKey);
  useSEO(pageKey);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 500], [0, 80]);
  const heroImg = useSiteImage(cfg.heroKey, cfg.heroFallback);
  const [works, setWorks] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`, { params: { category: cfg.service } })
      .then((r) => setWorks((r.data || []).filter((w) => cfg.cityRegex.test(w.location || ''))))
      .catch(() => {});
  }, [cfg]);

  // FAQPage structured data — RO content, the target market is Google Romania
  useEffect(() => {
    const id = `ld-faq-${pageKey}`;
    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: translations.ro[cfg.tKey].faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el.textContent = ''; };
  }, [pageKey, cfg.tKey]);

  return (
    <main data-testid={`local-page-${pageKey}`}>
      <section className="relative min-h-[80svh] flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
          <motion.img
            src={heroImg}
            alt={lp.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        </motion.div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}>
            <Overline className="mb-8">{lp.overline}</Overline>
          </motion.div>
          <MaskedTitle
            lines={[lp.title]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] mb-8 max-w-3xl text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
            className="text-white/75 text-base md:text-lg max-w-2xl leading-relaxed mb-10"
          >
            {lp.desc}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }}>
            <a
              href="#local-form"
              data-testid={`${pageKey}-cta`}
              className="inline-flex items-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-accenth transition-colors duration-300"
            >
              {t('nav.cta')} <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-line/10">
          {lp.points.map((b, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`${pageKey}-point-${i}`} className="bg-paper p-5 md:p-8 h-full hover:bg-surface transition-colors duration-500">
                <span className="font-display text-sm text-accent tracking-[0.2em]">0{i + 1}</span>
                <h3 className="font-display text-base md:text-lg font-medium mt-3 md:mt-5 mb-2 md:mb-3">{b.t}</h3>
                <p className="text-smoke text-xs md:text-sm leading-relaxed">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {cfg.service === 'pvc' ? (
        <>
          <NzebSection />
          <PvcProfiles />
        </>
      ) : cfg.service === 'rulouri' ? (
      <section className="bg-surface2 border-y border-line/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight max-w-xl">{lp.modelsTitle}</h2>
            <Link to={cfg.modelsLinkTo} data-testid={`${pageKey}-models-link`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent hover:gap-4 transition-all duration-300">
              {lp.modelsLink} <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid gap-5 grid-cols-2 lg:grid-cols-4 items-stretch">
            {t('rulouri').systems.map((sys, i) => (
              <Reveal key={sys.name} delay={i * 0.06} className="h-full">
                <div data-testid={`${pageKey}-model-${i}`} className="group bg-white border border-line/10 rounded-[2rem] overflow-hidden hover:shadow-[0_24px_70px_rgba(24,26,32,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-500 h-full flex flex-col">
                  <div className="flex-1 min-h-0 aspect-[3/4] grid place-items-center p-5 md:p-6">
                    <img src={`/rulouri-system-${i}.webp`} alt={sys.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.05]" />
                  </div>
                  <div className="px-5 pb-6 text-center min-h-[76px] flex flex-col justify-center">
                    <p className="font-display font-medium text-sm md:text-base">{sys.name}</p>
                    <p className="text-smoke text-xs mt-1">{sys.short}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      ) : (
      <section className="bg-surface2 border-y border-line/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight max-w-xl">{lp.modelsTitle}</h2>
            <Link to={cfg.modelsLinkTo} data-testid={`${pageKey}-models-link`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent hover:gap-4 transition-all duration-300">
              {lp.modelsLink} <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className={`grid gap-5 ${cfg.models.length > 4 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
            {cfg.models.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.06}>
                <div data-testid={`${pageKey}-model-${i}`} className="group bg-paper border border-line/10 p-5 hover:border-accent/50 transition-colors duration-300">
                  <div className="h-20 md:h-24 transition-transform duration-500 group-hover:scale-[1.05]">
                    <PanelSketch model={m.sketch} />
                  </div>
                  <p className="font-display font-medium text-sm mt-4 text-center">{m.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {works.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{lp.worksTitle}</h2>
            <Link to="/portofoliu" data-testid={`${pageKey}-works-link`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent hover:gap-4 transition-all duration-300">
              {t('nav.portofoliu')} <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {works.slice(0, 3).map((w, i) => (
              <Reveal key={w.id || i} delay={i * 0.08}>
                <div data-testid={`${pageKey}-work-${i}`} className="relative overflow-hidden group">
                  <img
                    src={resolveImg((w.images && w.images[0]) || w.image)}
                    alt={w.title}
                    loading="lazy"
                    decoding="async"
                    className={`w-full h-64 md:h-80 object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${i % 2 ? 'clip-frame-alt' : 'clip-frame'}`}
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-80" />
                  <span className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <span className="font-display text-lg font-medium block">{w.title}</span>
                    <span className="text-smoke text-xs uppercase tracking-[0.15em] flex items-center gap-2 mt-1.5">
                      <MapPin size={12} className="text-accent" /> {w.location}
                    </span>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="bg-surface2 border-y border-line/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="mb-4">FAQ</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{lp.faqTitle}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-line/10 border-y border-line/10">
              {lp.faq.map((f, i) => {
                const open = openFaq === i;
                return (
                  <li key={i} className="group">
                    <button
                      type="button"
                      data-testid={`${pageKey}-faq-${i}`}
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center gap-5 py-5 text-left"
                    >
                      <span
                        className={`w-8 h-8 grid place-items-center border transition-all duration-300 shrink-0 ${
                          open ? 'bg-accent text-white border-accent rotate-45' : 'border-line/15 text-accent group-hover:bg-accent group-hover:text-white'
                        }`}
                      >
                        <Plus size={14} />
                      </span>
                      <span className={`font-display text-base md:text-lg font-medium transition-colors duration-300 ${open ? 'text-accent' : ''}`}>{f.q}</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <p data-testid={`${pageKey}-faq-${i}-a`} className="text-smoke text-sm leading-relaxed pl-14 pb-6 max-w-xl">{f.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      <section id="local-form" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="mb-4">{t('nav.cta')}</Overline>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">{lp.ctaTitle}</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="bg-paper border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-10 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <LeadForm testIdPrefix={`${pageKey}-lead-form`} defaultServices={[cfg.service]} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
